import React, {
  useState, useCallback, useContext, useRef, useEffect
} from 'react';
import proptypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import classNames from 'classnames/bind';
import { VideoPlayButton } from './VideoPlayButton';
import './video-hero-image.scss';

const { string, bool } = proptypes;

const VideoHeroImage = React.memo(({
  imageUrl,
  altText,
  fullWidth,
  brandHero,
  videoClasses,
  isBentoHero,
  desktopVideoUrl,
  mobileVideoUrl,
  mobilePosterImageUrl,
  desktopPosterImageUrl,
  shouldLoopVideo
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showReplay, setShowReplay] = useState(false);

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const lastVideoSourcesRef = useRef({
    imageUrl,
    desktopVideoUrl,
    mobileVideoUrl
  });

  useEffect(() => {
    const isChanged = lastVideoSourcesRef.current.imageUrl !== imageUrl
      || lastVideoSourcesRef.current.desktopVideoUrl !== desktopVideoUrl
      || lastVideoSourcesRef.current.mobileVideoUrl !== mobileVideoUrl;

    if (isChanged) {
      lastVideoSourcesRef.current = {
        imageUrl,
        desktopVideoUrl,
        mobileVideoUrl
      };
    }
  }, [imageUrl, desktopVideoUrl, mobileVideoUrl]);

  const handlePlayPause = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsPlaying((prev) => {
      if (prev) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      return !prev;
    });
    if (showReplay) setShowReplay(false);
  }, [showReplay]);

  const handleVideoEnded = () => {
    if (!shouldLoopVideo) {
      setShowReplay(true);
      setIsPlaying(false);
    }
  };

  const containerClasses = classNames(
    'video-hero-image sui-overflow-hidden sui-relative',
    {
      'sui-w-full': fullWidth,
      'sui-h-full': isBentoHero,
      'sui-row-start-1 md:sui-row-start-auto': brandHero,
    }
  );

  const sources = isBentoHero
    ? [
      <source
        key="desktop"
        className="sui-w-full sui-top-0 sui-left-0"
        src={lastVideoSourcesRef.current.desktopVideoUrl}
        type="video/mp4"
        media="(min-width: 1024px)"
        alt={altText}
      />,
      <source
        key="mobile"
        className="sui-w-full sui-top-0 sui-left-0"
        src={lastVideoSourcesRef.current.mobileVideoUrl}
        type="video/mp4"
        alt={altText}
      />
    ]
    : [
      <source key="main" src={lastVideoSourcesRef.current.imageUrl} type="video/mp4" alt={altText} />
    ];

  return (
    <div
      className={containerClasses}
    >
      <video
        className={videoClasses}
        aria-label={altText}
        autoPlay
        onEnded={handleVideoEnded}
        playsInline
        muted
        poster={isMobile ? mobilePosterImageUrl : desktopPosterImageUrl}
        loop={shouldLoopVideo}
        ref={videoRef}
      >
        {sources}
      </video>
      <VideoPlayButton isPlaying={isPlaying} shouldShowReplay={showReplay} handlePlayChange={handlePlayPause} />
    </div>
  );
});

VideoHeroImage.propTypes = {
  imageUrl: string,
  altText: string,
  fullWidth: bool,
  brandHero: bool,
  videoClasses: string,
  isBentoHero: bool,
  desktopVideoUrl: string,
  mobileVideoUrl: string,
  mobilePosterImageUrl: string,
  desktopPosterImageUrl: string,
  shouldLoopVideo: bool,
};

VideoHeroImage.defaultProps = {
  imageUrl: '',
  altText: '',
  fullWidth: false,
  brandHero: false,
  videoClasses: '',
  isBentoHero: false,
  desktopVideoUrl: '',
  mobileVideoUrl: '',
  mobilePosterImageUrl: null,
  desktopPosterImageUrl: null,
  shouldLoopVideo: true,
};

VideoHeroImage.displayName = 'VideoHeroImage';

export { VideoHeroImage };